import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  Observable,
  type Operation,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { print } from "graphql";
import { getAccessToken } from "./auth";

const graphqlUrl =
  import.meta.env.VITE_GRAPHQL_URL || "http://localhost:4000/graphql";

/**
 * Standard HTTP link for regular (non-upload) operations.
 */
const httpLink = createHttpLink({
  uri: graphqlUrl,
  credentials: "include",
});

/**
 * Upload link: handles File objects in mutation variables using the
 * GraphQL multipart request spec (https://github.com/jaydenseric/graphql-multipart-request-spec).
 *
 * If no File is detected in variables, it falls through to httpLink.
 * Referenced from tools/roster-importer/src/lib/apollo.ts but adapted for
 * in-memory auth tokens (no localStorage) and Apollo Client v3.
 */
const uploadLink = new ApolloLink((operation: Operation, forward) => {
  const hasFile = Object.values(operation.variables ?? {}).some(
    (v) => v instanceof File
  );

  if (!hasFile) {
    // No file — delegate to next link (httpLink)
    return forward(operation);
  }

  // Build multipart/form-data request per the spec
  return new Observable((observer) => {
    const context = operation.getContext();
    const headers: Record<string, string> = { ...(context.headers ?? {}) };

    // Inject auth token
    const token = getAccessToken();
    if (token) {
      headers["authorization"] = `Bearer ${token}`;
    }

    const formData = new FormData();
    const cleanVariables: Record<string, unknown> = {};
    const fileMap: Record<string, string[]> = {};
    const files: Array<{ index: string; file: File }> = [];
    let fileIndex = 0;

    for (const [key, value] of Object.entries(operation.variables ?? {})) {
      if (value instanceof File) {
        cleanVariables[key] = null;
        fileMap[String(fileIndex)] = [`variables.${key}`];
        files.push({ index: String(fileIndex), file: value });
        fileIndex++;
      } else {
        cleanVariables[key] = value;
      }
    }

    // Order matters: operations → map → files
    formData.append(
      "operations",
      JSON.stringify({
        query: print(operation.query),
        variables: cleanVariables,
        operationName: operation.operationName,
      })
    );
    formData.append("map", JSON.stringify(fileMap));
    for (const { index, file } of files) {
      formData.append(index, file);
    }

    fetch(graphqlUrl, {
      method: "POST",
      headers, // Do NOT set Content-Type — browser sets multipart boundary
      credentials: "include",
      body: formData,
    })
      .then(async (response) => {
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Upload HTTP ${response.status}: ${text}`);
        }
        return response.json();
      })
      .then((result) => {
        observer.next(result);
        observer.complete();
      })
      .catch((err) => {
        observer.error(err);
      });
  });
});

/**
 * Auth link: injects the in-memory access token into every GraphQL request.
 * The token is stored in a JS closure — never in localStorage (XSS-safe).
 */
const authLink = setContext((_, { headers }) => {
  const token = getAccessToken();
  return {
    headers: {
      ...headers,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  };
});

/**
 * Error link: handle 401 responses (token expired).
 * The AuthContext proactive refresh handles most cases,
 * but this catches edge cases where the token expires mid-request.
 *
 * Every error (GraphQL or network) is also logged to the DevTools console so
 * non-auth failures surface during debugging instead of being silently swallowed
 * by callers that only read `data` from `useQuery`. Without this floor, a
 * resolver throwing FORBIDDEN, a schema/data mismatch, or a 500 looks identical
 * to a clean empty result in the UI.
 */
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const code = err.extensions?.code;
      // eslint-disable-next-line no-console -- intentional floor for non-auth GraphQL errors; see block comment above
      console.error(`[Apollo] ${operation.operationName} failed`, {
        code,
        message: err.message,
        path: err.path,
      });
      if (
        code === "UNAUTHENTICATED" ||
        err.message?.toLowerCase().includes("not authenticated")
      ) {
        // Redirect to login — the AuthContext will handle re-auth
        window.location.href = "/login";
        return;
      }
    }
  }
  if (networkError) {
    // eslint-disable-next-line no-console -- intentional floor for network failures; see block comment above
    console.error(
      `[Apollo] ${operation.operationName} network error`,
      networkError,
    );
    if ("statusCode" in networkError && networkError.statusCode === 401) {
      window.location.href = "/login";
    }
  }
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, uploadLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
});
